import React, { useContext, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import GuidelineNotice from "../component/GuidelineNotice/GuidelineNotice";
import DeliveryInformationForm from "../component/DeliveryInformationForm/DeliveryInformationForm";
import BuyBackForm from "../component/BuyBackForm/BuyBackForm";
import DeferButton from "../component/DeferButton/DeferButton";
import {
  AddressConfirmStatusEnum,
  DeliveryModeEnum,
  LaptopRenewalFormPage,
  LaptopReplacementStatesEnum,
  RequestStatusEnum,
  StringFulBooleanValue,
} from "../../../helper/constant";
import ConformButton from "../component/ConfirmButton/ConformButton";
import { AssetWithState } from "../../AssetList/AssetList";
import { SubmitHandler, useForm } from "react-hook-form";
import { UseUpdateReplacementRequest } from "../async/useUpdateReplacementRequest";
import { AssetReplacementListContext } from "../../HardwareDetails/HardwareDetails";
import { UpdateReplacementRequestBody } from "../../../resource/bff";
import { LaptopRenewalFormValue } from "../LaptopReplacementModal";
import CancelButton from "../component/CancelButton/CancelButton";
import BackButton from "../component/BackButton/BackButton";
import CloseIconButton from "../component/CloseIconButton/CloseIconButton";

type Props = {
  currentAsset: AssetWithState;
  handleCloseModal: () => void;
  setIsAlertStatus: (val: RequestStatusEnum) => void;
  setAlertMessageOpen: () => void;
  setLoadingStatus: (val: boolean) => void;
  editStatus: boolean;
  changeEditStatus: (val: boolean) => void;
  openDeferReplacementModal: () => void;
};

const LaptopRenewalForm = ({
  currentAsset,
  handleCloseModal,
  setIsAlertStatus,
  setAlertMessageOpen,
  setLoadingStatus,
  editStatus,
  changeEditStatus,
  openDeferReplacementModal,
}: Props) => {
  const reconfirmEditStatus = useMemo(
    () =>
      currentAsset?.details?.reconfirmationStatus ===
        AddressConfirmStatusEnum.INITIATED && editStatus,
    [currentAsset]
  );

  const { isContactInfoError, isUserLocationsError } = useContext(
    AssetReplacementListContext
  );

  const isDeliveryLocationValid = useMemo(
    () =>
      reconfirmEditStatus &&
      (currentAsset.deliveryMode === DeliveryModeEnum.COLLECT_AT_OFFICE
        ? !isUserLocationsError
        : currentAsset.deliveryMode === DeliveryModeEnum.BOTH
        ? !isContactInfoError && !isUserLocationsError
        : !isContactInfoError),
    [
      currentAsset,
      reconfirmEditStatus,
      isUserLocationsError,
      isContactInfoError,
    ]
  );

  const {
    getValues,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<LaptopRenewalFormValue>({
    defaultValues: {
      deliveryLocation: isDeliveryLocationValid
        ? {
            state: currentAsset.details.state,
            city: currentAsset.details.city,
            country: currentAsset.details.country,
            postalCode: currentAsset.details.postalCode,
            mailingAddress: currentAsset.details.mailingAddress,
            optionTitle: `${currentAsset.details.country} - ${currentAsset.details.city}`,
          }
        : undefined,
      contactNumber:
        reconfirmEditStatus && !isContactInfoError
          ? currentAsset.details.contactNumber
          : undefined,
      isBuyOldLaptop: undefined,
    },
  });
  const { mutate } = UseUpdateReplacementRequest();
  const [currentPage, setCurrentPage] = useState<LaptopRenewalFormPage>(
    LaptopRenewalFormPage.DELIVERY_INFORMATION
  );

  const { refetchAssetReplacements } = useContext(AssetReplacementListContext);

  const isDeliveryInfoForm = useMemo(
    () => Boolean(currentPage === LaptopRenewalFormPage.DELIVERY_INFORMATION),
    [currentPage]
  );

  const isBuyBackForm = useMemo(
    () =>
      Boolean(
        currentPage === LaptopRenewalFormPage.LAPTOP_BUYBACK &&
          currentAsset?.buyBackLink
      ),
    [currentPage]
  );

  const handleCloseForm = () => {
    handleCloseModal();
    reset();
    setCurrentPage(LaptopRenewalFormPage.DELIVERY_INFORMATION);
  };

  const showDeferButton = useMemo(
    () =>
      Boolean(
        currentAsset?.allowDeferReplacement === StringFulBooleanValue.TRUE &&
          currentAsset?.state === LaptopReplacementStatesEnum.WAITING_RESPONSE
      ),
    [currentAsset]
  );

  const getSubmitButtonText = (
    reconfirmEditStatus: boolean,
    currentAsset: AssetWithState,
    currentPage: LaptopRenewalFormPage
  ) => {
    if (reconfirmEditStatus) {
      return "Confirm";
    }

    if (
      currentAsset?.isBuybackPreferenceEnabled === StringFulBooleanValue.TRUE
    ) {
      return currentPage === LaptopRenewalFormPage.LAPTOP_BUYBACK
        ? "Confirm preferences"
        : "Next";
    }

    return "Confirm preferences";
  };

  const onSubmit: SubmitHandler<LaptopRenewalFormValue> = (data) => {
    const buildRequestBody: UpdateReplacementRequestBody = {
      requestId: currentAsset.requestId,
      country: data.deliveryLocation.country,
      city: data.deliveryLocation.city,
      postalCode: data.deliveryLocation.postalCode,
      state: data.deliveryLocation.state,
      mailingAddress: data.deliveryLocation.mailingAddress,
      contactNumber: data.contactNumber,
      isBuyOldLaptop: reconfirmEditStatus
        ? currentAsset.details.buyBackOption
        : data.isBuyOldLaptop,
    };
    setLoadingStatus(true);
    mutate(buildRequestBody, {
      onSuccess: (data) => {
        refetchAssetReplacements().then(() => {
          setLoadingStatus(false);
          if (reconfirmEditStatus) {
            changeEditStatus(false);
          }
        });
      },
      onError: (error) => {
        setLoadingStatus(false);
        setIsAlertStatus(RequestStatusEnum.FAILURE);
        setAlertMessageOpen();
      },
      onSettled: () => {},
    });
  };

  const handleClickConfirmBtn = (asset: AssetWithState) => {
    if (
      asset?.isBuybackPreferenceEnabled === StringFulBooleanValue.TRUE &&
      currentPage === LaptopRenewalFormPage.DELIVERY_INFORMATION &&
      !reconfirmEditStatus
    ) {
      setCurrentPage(LaptopRenewalFormPage.LAPTOP_BUYBACK);
    }
  };

  const confirmBtmDisabled = useMemo(
    () =>
      currentPage === LaptopRenewalFormPage.DELIVERY_INFORMATION
        ? Boolean(getValues("deliveryLocation") && getValues("contactNumber"))
        : isValid,
    [currentPage, getValues, isValid]
  );
  return (
    <>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            color={"#634F7D"}
            fontFamily={"Inter"}
            fontWeight={"700"}
            fontSize={"24px"}
          >
            {reconfirmEditStatus
              ? "Delivery Preference Confirmation"
              : "Laptop Renewal"}
          </Typography>
          <Box sx={{ display: "flex", gap: "24px", alignItems: "center" }}>
            {Boolean(showDeferButton && !editStatus) && (
              <DeferButton handleClick={() => openDeferReplacementModal()} />
            )}
            <CloseIconButton
              handleClick={() => {
                handleCloseForm();
                setCurrentPage(LaptopRenewalFormPage.DELIVERY_INFORMATION);
              }}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography
          id="modal-description"
          fontFamily={"Inter"}
          fontWeight={"600"}
          fontSize={"20px"}
        >
          {isDeliveryInfoForm ? "Delivery Information" : "Laptop Buyback"}
        </Typography>
      </Grid>
      {isDeliveryInfoForm && (
        <Grid item xs={12}>
          <GuidelineNotice />
        </Grid>
      )}
      <Grid item xs={12}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <DeliveryInformationForm
              isShow={isDeliveryInfoForm}
              getValues={getValues}
              control={control}
              watch={watch}
              currentAsset={currentAsset}
            />
            <BuyBackForm
              isShow={isBuyBackForm}
              control={control}
              currentAsset={currentAsset}
            />
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
            gap="32px"
            sx={{
              marginTop: "48px",
            }}
          >
            <CancelButton
              handleClick={() => {
                handleCloseForm();
                setCurrentPage(LaptopRenewalFormPage.DELIVERY_INFORMATION);
              }}
            />
            <BackButton
              disabled={reconfirmEditStatus ? false : !isBuyBackForm}
              handleClick={() => {
                if (reconfirmEditStatus) {
                  changeEditStatus(false);
                } else {
                  setCurrentPage(LaptopRenewalFormPage.DELIVERY_INFORMATION);
                }
              }}
            />
            <ConformButton
              key={currentPage}
              text={getSubmitButtonText(
                reconfirmEditStatus,
                currentAsset,
                currentPage
              )}
              disabled={!confirmBtmDisabled}
              handleClick={() => handleClickConfirmBtn(currentAsset)}
            />
          </Grid>
        </form>
      </Grid>
    </>
  );
};

export default LaptopRenewalForm;
